import React from 'react';
import { Stack, Typography } from '@mui/material';
import { Modal, StatusChip, ToolbarButton, type StatusTone } from '../../../ui-kit';

export interface ConnectionModalProps {
  open: boolean;
  onClose: () => void;
  name: string;
  /** What this connection is for, in a line. */
  purpose: string;
  state: { tone: StatusTone; label: string };
  /** How it is set up, as facts. Changed where the connection lives, not here. */
  facts: { label: string; value: string }[];
  /** What has come through it, shown as a strip under the facts. */
  activity?: { label: string; value: string }[];
  /** Something wrong, said plainly above everything else. */
  problem?: string | null;
  /** The one thing to do next: "Manage in Connectors". */
  action?: { label: string; onClick: () => void };
}

/**
 * One connection, opened up: what it is joined to, how, and what has come
 * through it today.
 *
 * Read-only on purpose. A connection is changed where it lives — the
 * Connectors page for a CRM or a warehouse — so this shows the truth about it
 * and points there, rather than offering fields that would save nowhere.
 */
export default function ConnectionModal({
  open, onClose, name, purpose, state, facts, activity = [], problem, action,
}: ConnectionModalProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={name}
      subtitle={purpose}
      size="md"
      actions={<StatusChip tone={state.tone} label={state.label} />}
      footer={
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <ToolbarButton variant="ghost" onClick={onClose}>Close</ToolbarButton>
          {action ? <ToolbarButton variant="brand" onClick={action.onClick}>{action.label}</ToolbarButton> : null}
        </Stack>
      }
    >
      <Stack spacing={2}>
        {problem ? (
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.semantic.danger })}>{problem}</Typography>
        ) : null}

        <Stack spacing={1.25}>
          {facts.map((fact) => (
            <Stack key={fact.label} direction="row" alignItems="baseline" justifyContent="space-between" spacing={2}>
              <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary, minWidth: 140 })}>
                {fact.label}
              </Typography>
              <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, textAlign: 'right' })}>
                {fact.value}
              </Typography>
            </Stack>
          ))}
        </Stack>

        {activity.length ? (
          <Stack
            direction="row"
            spacing={3}
            sx={(t) => ({ p: 1.5, borderRadius: `${t.ds.radius.md}px`, bgcolor: t.tokens.surface.sunken, flexWrap: 'wrap' })}
          >
            {activity.map((fact) => (
              <Stack key={fact.label}>
                <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{fact.label}</Typography>
                <Typography variant="subtitle2" sx={(t) => ({ color: t.tokens.text.primary, fontVariantNumeric: 'tabular-nums' })}>
                  {fact.value}
                </Typography>
              </Stack>
            ))}
          </Stack>
        ) : null}
      </Stack>
    </Modal>
  );
}
